import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useWorkspace } from "../app/workspace";
import { timeAgo } from "../lib/format";
import { Icon } from "../components/icons";
import { SignalDot, Empty } from "../components/ui";
import type { SignalBand } from "../lib/types";

type BandFilter = SignalBand | "all";
type SortMode = "impact" | "recent";

const bandRank: Record<SignalBand, number> = { risk: 0, watch: 1, positive: 2 };
const bandMeta: Record<SignalBand, { cls: string; label: string }> = {
  risk: { cls: "badge--risk", label: "Risk" },
  watch: { cls: "badge--watch", label: "Watch" },
  positive: { cls: "badge--good", label: "Positive" },
};

const bandChips: { key: BandFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "risk", label: "Risk" },
  { key: "watch", label: "Watch" },
  { key: "positive", label: "Positive" },
];

export default function Signals() {
  const { ws } = useWorkspace();
  const [band, setBand] = useState<BandFilter>("all");
  const [source, setSource] = useState<string>("all");
  const [sort, setSort] = useState<SortMode>("impact");

  const sources = useMemo(
    () => Array.from(new Set(ws.signals.map((s) => s.source))).sort(),
    [ws.signals]
  );

  // Stats are computed across the whole portfolio, independent of filters.
  const open = ws.signals.filter((s) => s.status !== "resolved");
  const atRiskImpact = open
    .filter((s) => s.impact < 0)
    .reduce((sum, s) => sum + s.impact, 0);
  const positiveCount = ws.signals.filter((s) => s.band === "positive").length;
  const resolvedCount = ws.signals.filter((s) => s.status === "resolved").length;

  const filtered = useMemo(() => {
    const list = ws.signals.filter(
      (s) =>
        (band === "all" || s.band === band) &&
        (source === "all" || s.source === source)
    );
    return [...list].sort((a, b) => {
      if (sort === "impact") {
        if (bandRank[a.band] !== bandRank[b.band]) return bandRank[a.band] - bandRank[b.band];
        return Math.abs(b.impact) - Math.abs(a.impact);
      }
      return +new Date(b.at) - +new Date(a.at);
    });
  }, [ws.signals, band, source, sort]);

  return (
    <div className="page">
      <div className="mb-24">
        <p className="eyebrow">Signals</p>
        <h1 style={{ fontFamily: "var(--serif)", fontSize: 28, marginTop: 6 }}>
          The penumbra
        </h1>
        <p className="page__intro" style={{ marginBottom: 0 }}>
          Faint changes across the portfolio, ranked by impact. Act before they
          harden into churn.
        </p>
      </div>

      <div className="stats">
        <div className="stat">
          <div className="stat__label"><Icon name="pulse" size={14} /> Open signals</div>
          <div className="stat__value">{open.length}</div>
          <div className="stat__foot">{filtered.length} shown with current filters</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="arrowDown" size={14} /> At-risk impact</div>
          <div className="stat__value" style={{ color: "var(--risk)" }}>
            {atRiskImpact}<small> pts</small>
          </div>
          <div className="stat__foot">Sum of negative impact across open signals</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="arrowUp" size={14} /> Positive signals</div>
          <div className="stat__value" style={{ color: "var(--good)" }}>{positiveCount}</div>
          <div className="stat__foot">Expansion and advocacy worth chasing</div>
        </div>
        <div className="stat">
          <div className="stat__label"><Icon name="check" size={14} /> Resolved this week</div>
          <div className="stat__value">{resolvedCount}</div>
          <div className="stat__foot">Closed out by the team</div>
        </div>
      </div>

      <div className="toolbar">
        {bandChips.map((c) => (
          <button
            key={c.key}
            className={`chip ${band === c.key ? "chip--active" : ""}`}
            onClick={() => setBand(c.key)}
          >
            {c.key !== "all" && <SignalDot band={c.key} />}
            {c.label}
          </button>
        ))}
        <span style={{ width: 1, height: 22, background: "var(--line)", margin: "0 4px" }} />
        <span className="row" style={{ gap: 8 }}>
          <Icon name="filter" size={14} className="faint" />
          <select
            className="input"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            style={{ padding: "6px 10px" }}
          >
            <option value="all">All sources</option>
            {sources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </span>
        <div className="seg-control" style={{ marginLeft: "auto" }}>
          <button className={sort === "impact" ? "on" : ""} onClick={() => setSort("impact")}>
            By impact
          </button>
          <button className={sort === "recent" ? "on" : ""} onClick={() => setSort("recent")}>
            Most recent
          </button>
        </div>
      </div>

      <div className="grid-main">
        <div className="card">
          <div className="card__hd">
            <div>
              <h3>Signal inbox</h3>
              <span className="sub">
                {sort === "impact" ? "Risk first, then by magnitude" : "Newest first"}
              </span>
            </div>
            <span className="tag">{filtered.length} signals</span>
          </div>
          <div className="card__bd" style={{ paddingTop: 4, paddingBottom: 4 }}>
            {filtered.length === 0 ? (
              <Empty>No signals match these filters.</Empty>
            ) : (
              filtered.map((s) => (
                <div
                  className="feed-item"
                  key={s.id}
                  style={{ gridTemplateColumns: "8px 1fr auto", alignItems: "start", gap: 14 }}
                >
                  <SignalDot band={s.band} />
                  <div>
                    <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                      <span className={`badge ${bandMeta[s.band].cls}`}>{bandMeta[s.band].label}</span>
                      <b style={{ fontSize: 14 }}>{s.title}</b>
                    </div>
                    <small className="muted" style={{ display: "block", marginTop: 4 }}>
                      {s.detail}
                    </small>
                    <div className="row" style={{ gap: 10, marginTop: 9, flexWrap: "wrap" }}>
                      <Link to={`/accounts/${s.accountId}`} className="row" style={{ gap: 6 }}>
                        <Icon name="building" size={13} className="faint" />
                        <b style={{ fontSize: 12.5 }}>{s.accountName}</b>
                      </Link>
                      <span className="tag">{s.source}</span>
                      <span className="faint" style={{ fontSize: 12, textTransform: "capitalize" }}>
                        {s.status}
                      </span>
                      {s.suggestedPlaybook && (
                        <Link to="/playbooks" className="btn btn--ghost btn--sm">
                          <Icon name="bolt" size={13} /> Run: {s.suggestedPlaybook}
                        </Link>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div
                      className="num"
                      style={{
                        fontWeight: 600,
                        fontSize: 16,
                        color: s.impact < 0 ? "var(--risk)" : "var(--good)",
                      }}
                    >
                      {s.impact > 0 ? "+" : ""}{s.impact}
                    </div>
                    <time>{timeAgo(s.at)} ago</time>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ display: "grid", gap: 20 }}>
          <div className="card">
            <div className="card__hd">
              <h3>Reading the penumbra</h3>
            </div>
            <div className="card__bd">
              <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                Leading indicators surface weeks before a renewal is decided — a
                sponsor departs, adoption plateaus, an invoice slips. Caught in
                the half-light, they are still cheap to fix.
              </p>
              <div className="divider" />
              <p className="eyebrow" style={{ marginBottom: 10 }}>By band</p>
              {bandChips
                .filter((c) => c.key !== "all")
                .map((c) => {
                  const key = c.key as SignalBand;
                  const count = ws.signals.filter((s) => s.band === key).length;
                  return (
                    <div
                      key={key}
                      className="row between"
                      style={{ padding: "7px 0", borderBottom: "1px solid var(--line-soft)" }}
                    >
                      <span className="row" style={{ gap: 8 }}>
                        <SignalDot band={key} />
                        <span className={`badge ${bandMeta[key].cls}`}>{bandMeta[key].label}</span>
                      </span>
                      <b className="num" style={{ fontSize: 15 }}>{count}</b>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
